import * as React from 'react';

import { SignedNumber } from '@dndbeyond/character-common-components/es';
import {
    Ability,
    AbilityUtils,
    CharacterPreferences,
    CharacterTheme,
    Constants,
} from '@dndbeyond/character-rules-engine/es';
import { DiceTools, IRollContext, RollType } from '@dndbeyond/dice';
import { GameLogContext } from '@dndbeyond/game-log-components';

import BoxBackground from '../BoxBackground';
import { DigitalDiceWrapper, SignedNumberDice } from '../Dice';
import AbilityScoreBoxSvg from '../Svg/boxes/AbilityScoreBoxSvg';

interface Props {
    className: string;
    ability: Ability;
    preferences: CharacterPreferences;
    theme: CharacterTheme;
    onClick?: (ability: Ability) => void;
    diceEnabled: boolean;
    rollContext: IRollContext;
}
class AbilitySummary extends React.PureComponent<Props> {
    static defaultProps = {
        className: '',
        diceEnabled: false,
    };

    handleClick = (evt: React.MouseEvent): void => {
        const { onClick, ability } = this.props;

        if (onClick) {
            evt.stopPropagation();
            evt.nativeEvent.stopImmediatePropagation();
            onClick(ability);
        }
    };

    render() {
        const { className, ability, preferences, theme, diceEnabled, rollContext } = this.props;

        const [{ messageTargetOptions, defaultMessageTargetOption, userId }] = this.context;

        let classNames: Array<string> = [className, 'ddbc-ability-summary'];

        let primaryNode: React.ReactNode;
        let secondaryNode: React.ReactNode;
        const abilityScore = AbilityUtils.getScore(ability);
        const abilityModifier = AbilityUtils.getModifier(ability);

        if (preferences.abilityScoreDisplayType === Constants.PreferenceAbilityScoreDisplayTypeEnum.SCORES_TOP) {
            primaryNode = (
                <DigitalDiceWrapper
                    diceNotation={DiceTools.CustomD20(abilityModifier === null ? 0 : abilityModifier)}
                    rollType={RollType.Check}
                    rollAction={AbilityUtils.getName(ability)}
                    diceEnabled={diceEnabled}
                    advMenu={true}
                    themeColor={theme.themeColor}
                    rollContext={rollContext}
                    rollTargetOptions={messageTargetOptions ? Object.values(messageTargetOptions?.entities) : undefined}
                    rollTargetDefault={defaultMessageTargetOption}
                    userId={userId}
                >
                    {abilityScore}
                </DigitalDiceWrapper>
            );

            if (abilityModifier === null) {
                secondaryNode = '--';
            } else {
                secondaryNode = <SignedNumber number={abilityModifier} />;
            }
        } else {
            if (abilityModifier === null) {
                primaryNode = '--';
            } else {
                primaryNode = (
                    <SignedNumberDice
                        number={abilityModifier}
                        size={'large'}
                        diceNotation={DiceTools.CustomD20(abilityModifier)}
                        rollType={RollType.Check}
                        rollAction={AbilityUtils.getName(ability)}
                        diceEnabled={diceEnabled}
                        advMenu={true}
                        themeColor={theme.themeColor}
                        isDarkMode={theme.isDarkMode}
                        rollContext={rollContext}
                        rollTargetOptions={
                            messageTargetOptions ? Object.values(messageTargetOptions?.entities) : undefined
                        }
                        rollTargetDefault={defaultMessageTargetOption}
                        userId={userId}
                    />
                );
            }
            secondaryNode = abilityScore;
        }

        return (
            <div className={classNames.join(' ')} onClick={this.handleClick}>
                <BoxBackground StyleComponent={AbilityScoreBoxSvg} theme={theme} />
                <div className="ddbc-ability-summary__heading">
                    <span
                        className={`ddbc-ability-summary__label ${
                            theme.isDarkMode ? 'ddbc-ability-summary__label--dark-mode' : ''
                        }`}
                    >
                        {AbilityUtils.getLabel(ability)}
                    </span>
                    <span className="ddbc-ability-summary__abbr">{AbilityUtils.getStatKey(ability)}</span>
                </div>
                <div
                    className={`ddbc-ability-summary__primary ${
                        theme.isDarkMode ? 'ddbc-ability-summary__primary--dark-mode' : ''
                    }`}
                >
                    {primaryNode}
                </div>
                <div
                    className={`ddbc-ability-summary__secondary ${
                        theme.isDarkMode ? 'ddbc-ability-summary__secondary--dark-mode' : ''
                    }`}
                >
                    {secondaryNode}
                </div>
            </div>
        );
    }
}

AbilitySummary.contextType = GameLogContext;

export default AbilitySummary;
